import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * `<recently-viewed-products>` — hydrates the PDP "Recently viewed" carousel.
 *
 * The list of products lives client-side in localStorage (see
 * `recently-viewed-products.js`, which records the current product on every PDP
 * view). On connect we:
 *   1. read the stored product ids, drop the product currently being viewed,
 *   2. ask the Search route to render `sections/recently-viewed.liquid` for those
 *      ids via the Section Rendering API (`?q=id:… OR id:…&section_id=recently-viewed`),
 *      which is the same mechanism the predictive-search recently-viewed row uses,
 *   3. inject the returned cards (kept in localStorage order) and reveal the section.
 *
 * Renders nothing — and the section stays `display:none` — when there are no other
 * recently-viewed products, so there's never an orphan heading.
 */
class RecentlyViewedProducts extends HTMLElement {
  #loaded = false;

  connectedCallback() {
    // Avoid blocking first paint; hydrate once the main thread is idle.
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this.#load());
    } else {
      setTimeout(() => this.#load(), 0);
    }
  }

  async #load() {
    if (this.#loaded) return;
    this.#loaded = true;

    const sectionId = this.dataset.sectionId;
    const list = this.querySelector('[data-rvp-list]');
    const section = this.closest('[data-rvp-section]');
    if (!sectionId || !list) return;

    const currentId = this.dataset.productId;
    const max = parseInt(this.dataset.max || '4', 10);

    const ids = RecentlyViewed.getProducts()
      .filter((id) => String(id) !== String(currentId))
      .slice(0, max);

    if (ids.length === 0) return;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const cards = doc.querySelector('[data-rvp-cards]');

      // No live products resolved (all viewed ids deleted/unpublished). Clear any
      // anti-CLS skeletons the inline reveal script injected and re-hide the section.
      if (!cards || cards.children.length === 0) {
        list.innerHTML = '';
        section?.setAttribute('data-has-products', 'false');
        return;
      }

      // Swap skeletons (if any) for the real cards in the already-reserved space.
      list.innerHTML = cards.innerHTML;
      section?.setAttribute('data-has-products', 'true');
    } catch (error) {
      // Roll back the skeleton reveal so a failed fetch doesn't strand placeholders.
      list.innerHTML = '';
      section?.setAttribute('data-has-products', 'false');
      // eslint-disable-next-line no-console
      console.error('recently-viewed: section fetch failed', error);
    }
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
